import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { useAuth } from './AuthContext';
import { setEmpresaCurrencySymbol } from '../utils/dashboardHelpers';

// Contexto para la información de la empresa
export const EmpresaContext = createContext();

// Símbolos de moneda por defecto según código de país
const MONEDAS_DEFAULT = {
  PE: 'S/',
  CO: '$',
  CL: '$',
  MX: '$',
  CR: '₡',
  EC: '$',
  US: '$'
};

export const EmpresaProvider = ({ children }) => {
  const { user } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar información de la empresa cuando el usuario esté autenticado
  const cargarEmpresa = useCallback(async () => {
    if (!user?.token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${config.API_BASE_URL}/api/v1/empresas/mi-empresa`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      const empresaData = response.data?.data || response.data;

      // Asegurar que siempre tengamos un símbolo de moneda
      if (!empresaData.simbolo_moneda) {
        empresaData.simbolo_moneda = MONEDAS_DEFAULT[empresaData.cod_pais] || 'S/';
      }

      console.log('✅ Empresa cargada:', empresaData.nombre_empresa, '- Moneda:', empresaData.simbolo_moneda);
      setEmpresa(empresaData);
      setError(null);

      // Sincronizar con dashboardHelpers para componentes que usan esa utilidad
      setEmpresaCurrencySymbol(empresaData.simbolo_moneda);
    } catch (err) {
      console.error('❌ Error al cargar empresa:', err);
      setError('Error al cargar información de la empresa');
      // Establecer valores por defecto para que la app siga funcionando
      setEmpresa({
        simbolo_moneda: 'S/',
        det_pais: 'Perú',
        cod_pais: 'PE'
      });
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    cargarEmpresa();
  }, [cargarEmpresa]);

  // Obtener el símbolo de moneda de la empresa
  const getSimboloMoneda = useCallback(() => {
    return empresa?.simbolo_moneda || 'S/';
  }, [empresa]);

  // Formatear un valor como moneda de la empresa
  const formatCurrency = useCallback((value, options = {}) => {
    const simbolo = getSimboloMoneda();
    const { decimals = 2, showSymbol = true } = options;

    const formattedValue = parseFloat(value || 0).toLocaleString('es-PE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    return showSymbol ? `${simbolo} ${formattedValue}` : formattedValue;
  }, [getSimboloMoneda]);

  // Formatear moneda de forma compacta (para gráficos)
  const formatCurrencyCompact = useCallback((value) => {
    const simbolo = getSimboloMoneda();
    const num = parseFloat(value || 0);

    if (num >= 1000000) {
      return `${simbolo} ${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${simbolo} ${(num / 1000).toFixed(1)}k`;
    }
    return `${simbolo} ${num.toFixed(2)}`;
  }, [getSimboloMoneda]);

  const value = {
    empresa,
    loading,
    error,
    recargarEmpresa: cargarEmpresa,
    getSimboloMoneda,
    formatCurrency,
    formatCurrencyCompact,
    // Propiedades directas de conveniencia
    simboloMoneda: empresa?.simbolo_moneda || 'S/',
    nombreEmpresa: empresa?.nombre_empresa || '',
    pais: empresa?.det_pais || '',
    codPais: empresa?.cod_pais || 'PE'
  };

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  );
};

// Hook para usar el contexto de empresa
export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (!context) {
    throw new Error('useEmpresa debe usarse dentro de un EmpresaProvider');
  }
  return context;
};

// Hook específico para formateo de moneda (para uso en componentes que solo necesitan esto)
export const useCurrency = () => {
  const { formatCurrency, formatCurrencyCompact, simboloMoneda, getSimboloMoneda } = useEmpresa();
  return { formatCurrency, formatCurrencyCompact, simboloMoneda, getSimboloMoneda };
};

export default EmpresaProvider;
